import { ref, Ref } from '@nuxtjs/composition-api';
import api from "~/config/extensionsApi";
import { getApiAxios } from "~/helpers";

// Import interfaces from useFAQ.ts
import {
    FaqQuestion,
    FaqResult,
    UseFAQInterface,
    FaqGroup,
    FaqGroupResult,
    UseFAQErrors,
} from './useFAQ';

export function useFAQ(): UseFAQInterface {
    const questions = ref<FaqResult | null>(null);
    const faqGroups = ref<FaqGroupResult | null>(null);
    const currentFaq = ref<FaqQuestion | null>(null);
    const currentFaqGroup = ref<FaqGroup | null>(null);
    const loading = ref<boolean>(false);
    const error = ref<UseFAQErrors>({
        fetchQuestion: null
    });

    const fetchQuestion = async (searchCriteria?: Record<string, any>): Promise<FaqResult | null> => {
        try {
            loading.value = true;
            
            // Build the URL with search criteria if provided
            let url = api.faq.faq_get_faqlist;
            if (searchCriteria) {
                const params = new URLSearchParams();
                Object.entries(searchCriteria).forEach(([key, value]) => {
                    params.append(key, value);
                });
                url = `${url}?${params.toString()}`;
            }
            
            const response = await getApiAxios(url);

            if (response?.code === 200) {
                questions.value = response.result;
                return response.result;
            }
            return null;
        } catch (err) {
            error.value.fetchQuestion = err as Error;
            console.error("Error fetching FAQ questions:", err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const searchQuestions = async (searchTerm: string, searchCriteria?: Record<string, any> | string): Promise<FaqResult | null> => {
        try {
            loading.value = true;
            let url = api.faq.faq_get_faqlist;
            
            if (typeof searchCriteria === 'string') {
                // If searchCriteria is passed as a string, use it directly
                url = `${url}?${searchCriteria}`;
            } else if (searchCriteria && Object.keys(searchCriteria).length > 0) {
                // If searchCriteria is passed as an object, convert it to URL params
                const params = new URLSearchParams();
                Object.entries(searchCriteria).forEach(([key, value]) => {
                    params.append(key, String(value));
                });
                url = `${url}?${params.toString()}`;
            } else if (searchTerm) {
                // Default behavior: search by title if no criteria is specified
                const params = new URLSearchParams();
                params.append('searchCriteria[filter_groups][0][filters][0][field]', 'title');
                params.append('searchCriteria[filter_groups][0][filters][0][value]', `%${searchTerm}%`);
                params.append('searchCriteria[filter_groups][0][filters][0][condition_type]', 'like');
                params.append('searchCriteria[filter_groups][1][filters][0][field]', 'content');
                params.append('searchCriteria[filter_groups][1][filters][0][value]', `%${searchTerm}%`);
                params.append('searchCriteria[filter_groups][1][filters][0][condition_type]', 'like');
                url = `${url}?${params.toString()}`;
            }
            
            console.log("Search URL:", url);
            const response = await getApiAxios(url);

            if (response?.code === 200) {
                return response.result;
            }
            return null;
        } catch (err) {
            error.value.fetchQuestion = err as Error;
            console.error("Error searching FAQ questions:", err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getFaqById = async (faqId: string): Promise<FaqQuestion | null> => {
        try {
            loading.value = true;
            const response = await getApiAxios(`${api.faq.faq_get_faqbyid}?id=${faqId}`);

            if (response?.code === 200) {
                currentFaq.value = response.result;
                return response.result;
            }
            return null;
        } catch (err) {
            error.value.fetchQuestion = err as Error;
            console.error("Error fetching FAQ by ID:", err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getFaqGroupList = async (searchCriteria?: Record<string, any>): Promise<FaqGroupResult | null> => {
        try {
            loading.value = true;
            
            // Build the URL with search criteria if provided
            let url = api.faq.get_faq_group_list;
            if (searchCriteria) {
                const params = new URLSearchParams();
                Object.entries(searchCriteria).forEach(([key, value]) => {
                    params.append(key, value);
                });
                url = `${url}?${params.toString()}`;
            }
            
            const response = await getApiAxios(url);

            if (response?.code === 200) {
                faqGroups.value = response.result;
                return response.result;
            }
            return null;
        } catch (err) {
            error.value.fetchQuestion = err as Error;
            console.error("Error fetching FAQ group list:", err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    const getFaqGroupById = async (groupId: string): Promise<FaqGroup | null> => {
        try {
            loading.value = true;
            const response = await getApiAxios(`${api.faq.get_faq_group_byid}?faqGroupId=${groupId}`);

            if (response?.code === 200) {
                currentFaqGroup.value = response.result;
                return response.result;
            }
            return null;
        } catch (err) {
            error.value.fetchQuestion = err as Error;
            console.error("Error fetching FAQ group by ID:", err);
            return null;
        } finally {
            loading.value = false;
        }
    };

    return {
        questions: questions as Ref<FaqResult | null>,
        faqGroups: faqGroups as Ref<FaqGroupResult | null>,
        currentFaq: currentFaq as Ref<FaqQuestion | null>,
        currentFaqGroup: currentFaqGroup as Ref<FaqGroup | null>,
        loading,
        error: error as Ref<UseFAQErrors>,
        fetchQuestion,
        searchQuestions,
        getFaqById,
        getFaqGroupList,
        getFaqGroupById
    };
}
